import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { HelpCircle } from 'lucide-react';

/**
 * Contextual help tooltip component
 * Usage: <HelpTooltip content="Explanation text here" />
 */
export function HelpTooltip({ 
  content, 
  children,
  side = 'top',
  className = '',
  iconClassName = 'w-4 h-4'
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children || (
            <button 
              type="button"
              className={`inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors ${className}`}
              aria-label="Help"
            >
              <HelpCircle className={iconClassName} />
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent 
          side={side} 
          className="max-w-xs text-sm leading-relaxed"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Help content definitions for the app
 * Centralized place for all contextual help text
 */
export const HelpContent = {
  // Mileage related
  mileage: {
    irsRate: "The IRS standard mileage rate for 2026 is $0.70 per mile. This is the amount you can deduct for each business mile driven.",
    businessMileage: "Business mileage includes trips for client meetings, errands for work supplies, traveling between work locations, and any driving done primarily for business purposes. Commuting from home to your regular workplace does NOT count.",
    roundTrip: "Enable this if you're logging a trip that goes to a destination and back. The distance will be doubled automatically for your deduction.",
    frequentTrips: "Save routes you drive often (like 'Home to Client Office') for one-tap logging. Great for recurring trips!",
    gpsTracking: "GPS auto-tracking uses your device's location to automatically record trip distance. Start tracking when you begin driving and stop when you arrive.",
  },
  
  // Transaction related
  transactions: {
    businessVsPersonal: "Business expenses are costs directly related to running your business (supplies, software, travel). Personal expenses are for non-business use. Only business expenses are tax-deductible.",
    pending: "Pending transactions need to be categorized. Swipe right for Business, left for Personal, or tap to assign a specific tax category.",
    taxCategory: "Tax categories correspond to Schedule C line items. Assigning the right category helps generate accurate tax reports.",
    tags: "Tags help you organize and filter transactions. Create custom tags like 'Q1 2026' or 'Project Alpha' for easy searching.",
  },
  
  // Receipt related
  receipts: {
    ocrProcessing: "Our AI scans your receipt image to automatically extract the vendor name, date, and total amount. Review the extracted data for accuracy.",
    matching: "We automatically try to match receipts to your transactions based on amount, date, and vendor. Matched receipts provide documentation for your expenses.",
    digitalShoebox: "Forward receipts to your unique email address and they'll automatically appear here. Great for email receipts!",
  },
  
  // Dashboard related
  dashboard: {
    scheduleC: "Schedule C is the IRS form for reporting business income and expenses. We track your data to help generate this form.",
    mileageLog: "A detailed record of all business miles driven. Required by the IRS to claim mileage deductions.",
    form1099: "1099 forms report income from freelance work, contracts, or other non-employee compensation.",
    writeoffProgress: "Tracks how much of your expenses have been properly categorized and documented for tax deductions.",
  },
  
  // Write-offs related
  writeoffs: {
    whatQualifies: "Write-offs are legitimate business expenses that reduce your taxable income. Common examples: office supplies, software subscriptions, business travel, professional services, and home office expenses.",
    homeOffice: "If you use part of your home exclusively for business, you may deduct a portion of rent/mortgage, utilities, and internet based on square footage.",
    documentation: "Keep receipts and records for all write-offs. The IRS may request documentation to verify your deductions.",
  },
  
  // Reports related
  reports: {
    scheduleCExport: "Generates a PDF with your income and expenses organized by Schedule C categories. Review before filing taxes.",
    dataExport: "Download all your data in CSV or Excel format for use in other accounting software or for your records.",
  },
  
  // Settings related
  settings: {
    autoSync: "Automatically imports new transactions from your connected bank accounts. Set a schedule that works for you.",
    notifications: "Get reminders for uncategorized transactions, tax deadlines, and weekly summaries.",
  },
};

export default HelpTooltip;
